import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { S3_BUCKET_URL } from '@/constants';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingSubtitle } from '@/pages/Onboarding/components/Subtitle';
import { useOnboarding } from '@/pages/Onboarding/context';
import { OwnAiTitle } from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { canLogEvents } from '@/utils/canLogEvents';

import { LureScreenFooter } from '../../components/LureScreenFooter';
import { LayoutDataType } from '../../types';

import { ScreenFindAiImageStyles, ScreenFindAiStyles } from './FindAi.styles';

const IMAGE_NUMBERS = [1, 2, 3, 4] as const;

const getImageSrc = (
  count: number,
  age: string,
  ethnicity: string,
  gender: LayoutDataType['gender'],
): string => {
  if (gender === 'male') {
    return `${S3_BUCKET_URL}/layout-own-ai-man/by_choise/${age}-${ethnicity}-${count}.png`;
  }
  return `${S3_BUCKET_URL}/layout-own-ai/ethnicity/${ethnicity}/${age}-${count}.png`;
};

const getBgUrl = (gender: LayoutDataType['gender']): string => {
  if (gender === 'male') {
    return `${S3_BUCKET_URL}/layout-own-ai-man/bg-circles.png`;
  }
  return `${S3_BUCKET_URL}/layout-own-ai/bg-circles.png`;
};

export const ScreenFindAi: React.FC = () => {
  const { wizard } = useOnboarding();
  const form = useFormContext<LayoutDataType>();
  const ethnicity = form.watch('ethnicity') ?? 'european';
  const age = form.watch('age') ?? '18-24';
  const gender = form.watch('gender');

  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'FindAi',
      });
    }
  }, []);

  const handleNextStep = (): void => {
    wizard.nextStep();
  };

  const bgUrl = getBgUrl(gender ?? 'female');
  const imageSrc = (count: number) =>
    getImageSrc(count, age, ethnicity, gender ?? 'female');

  return (
    <ScreenFindAiStyles $bgUrl={bgUrl}>
      <IonContent>
        <div className="images-container">
          {IMAGE_NUMBERS.map((number) => (
            <ScreenFindAiImageStyles key={number}>
              <img src={imageSrc(number)} alt="greet" />
            </ScreenFindAiImageStyles>
          ))}
        </div>
        <OwnAiTitle className="local-title">
          Let's find somebody who is
          <br /> perfect for you!
        </OwnAiTitle>
        <OnboardingSubtitle>
          We have a lot of AI's that met many different preferences for each
          user
        </OnboardingSubtitle>
      </IonContent>

      <LureScreenFooter onContinue={handleNextStep} />
    </ScreenFindAiStyles>
  );
};
